import React from 'react';
import { cn } from '../../lib/utils';
import { CheckCircle, AlertCircle, XCircle, Award } from 'lucide-react';

const ratingConfig = {
  outstanding: {
    label: 'Outstanding',
    className: 'rag-outstanding',
    bgColor: 'bg-emerald-50',
    textColor: 'text-emerald-700',
    borderColor: 'border-emerald-200',
    icon: Award,
  },
  good: {
    label: 'Good',
    className: 'rag-good',
    bgColor: 'bg-green-50',
    textColor: 'text-green-700',
    borderColor: 'border-green-200',
    icon: CheckCircle,
  },
  requires_improvement: {
    label: 'Requires Improvement',
    className: 'rag-requires-improvement',
    bgColor: 'bg-amber-50',
    textColor: 'text-amber-700',
    borderColor: 'border-amber-200',
    icon: AlertCircle,
  },
  inadequate: {
    label: 'Inadequate',
    className: 'rag-inadequate',
    bgColor: 'bg-red-50',
    textColor: 'text-red-700',
    borderColor: 'border-red-200',
    icon: XCircle,
  },
};

export const RAGBadge = ({ rating, size = 'default', showLabel = true, showIcon = true }) => {
  const config = ratingConfig[rating] || ratingConfig.good;
  const Icon = config.icon;

  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    default: 'px-3 py-1 text-sm',
    lg: 'px-4 py-1.5 text-base',
  };

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full font-medium border',
        config.bgColor,
        config.textColor,
        config.borderColor,
        sizeClasses[size]
      )}
      data-testid={`rag-badge-${rating}`}
    >
      {showIcon && <Icon className={cn(size === 'sm' ? 'w-3 h-3' : 'w-4 h-4')} />}
      {showLabel && <span>{config.label}</span>}
    </span>
  );
};

export const RAGScoreCircle = ({ score, rating, size = 'default' }) => {
  const config = ratingConfig[rating] || ratingConfig.good;
  
  const sizeClasses = {
    sm: 'w-16 h-16 text-xl',
    default: 'w-24 h-24 text-3xl',
    lg: 'w-32 h-32 text-4xl',
  };

  const gradientClasses = {
    outstanding: 'from-emerald-500 to-emerald-600',
    good: 'from-green-500 to-green-600',
    requires_improvement: 'from-amber-500 to-amber-600',
    inadequate: 'from-red-500 to-red-600',
  };

  return (
    <div
      className={cn(
        'rounded-full flex flex-col items-center justify-center text-white font-bold bg-gradient-to-br',
        sizeClasses[size],
        gradientClasses[rating] || gradientClasses.good
      )}
      data-testid={`rag-score-circle-${rating}`}
    >
      <span>{score?.toFixed(1) || '0.0'}</span>
      <span className={cn(size === 'sm' ? 'text-[8px]' : 'text-xs', 'font-normal opacity-80')}>
        / 4.0
      </span>
    </div>
  );
};

export default RAGBadge;
